<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from './supabase'
import {
  Users, FileText, Receipt, CreditCard, BarChart3, LayoutTemplate,
  UserCircle2, Search, LogOut, Building2, ShieldCheck, Inbox, Wallet,
  Scan, CalendarDays, HardHat, CircleDot, Check, ChevronsUpDown, ClipboardCheck
} from 'lucide-vue-next'
import { logAuditEvent, fetchMyOrgs, type OrgMembership } from './billing/api'
import { useOrgSettings, setCurrentRole } from './composables/useOrgSettings'
import FeedbackBubble from './components/FeedbackBubble.vue'

import {
  SidebarProvider, Sidebar, SidebarHeader, SidebarContent, SidebarFooter,
  SidebarGroup, SidebarGroupLabel, SidebarGroupContent,
  SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarInset, SidebarTrigger,
  SidebarRail
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuGroup
} from '@/components/ui/dropdown-menu'

declare const __APP_COMMIT__: string

const router = useRouter()
const route = useRoute()
const appCommit = __APP_COMMIT__
const session = ref<any>(null)
const sidebarOpen = ref(true)
function onSidebarClick() {
  if (!sidebarOpen.value) sidebarOpen.value = true
}
function onInsetPointerDown(e: PointerEvent) {
  if (!sidebarOpen.value) return
  const t = e.target as HTMLElement | null
  if (t && t.closest('[data-sidebar="trigger"]')) return
  sidebarOpen.value = false
}

const { org, loadOrgSettings, switchOrg, createOrg } = useOrgSettings()
const createOrgOpen = ref(false)
const createOrgForm = ref({ name: '', legal_name: '', address: '', gst_registered: false, gst_rate: 9 })
const createOrgErr = ref('')
const creatingOrg = ref(false)
async function submitCreateOrg() {
  if (!createOrgForm.value.name.trim()) { createOrgErr.value = 'Name is required'; return }
  creatingOrg.value = true
  createOrgErr.value = ''
  try {
    await createOrg({
      name: createOrgForm.value.name,
      legal_name: createOrgForm.value.legal_name || createOrgForm.value.name,
      address: createOrgForm.value.address || undefined,
      gst_registered: createOrgForm.value.gst_registered,
      gst_rate: createOrgForm.value.gst_registered ? Number(createOrgForm.value.gst_rate) || 0 : 0
    })
    createOrgOpen.value = false
    createOrgForm.value = { name: '', legal_name: '', address: '', gst_registered: false, gst_rate: 9 }
    router.go(0)
  } catch (e: any) {
    createOrgErr.value = e?.message || String(e)
  } finally {
    creatingOrg.value = false
  }
}
const myOrgs = ref<OrgMembership[]>([])
const activeOrgId = ref<string | null>(null)
const orgsLoading = ref(false)
async function loadMyOrgs() {
  if (orgsLoading.value) return
  orgsLoading.value = true
  try {
    const r = await fetchMyOrgs()
    myOrgs.value = r.orgs || []
    activeOrgId.value = r.active_org_id
    const active = myOrgs.value.find(o => o.id === r.active_org_id)
    setCurrentRole(active?.role || null)
  } catch { /* ignore */ }
  finally { orgsLoading.value = false }
}
const myRole = computed(() => myOrgs.value.find(o => o.id === activeOrgId.value)?.role || null)
const isTech = computed(() => myRole.value === 'technician')
async function onSwitchOrg(id: string) {
  if (id === activeOrgId.value) return
  try {
    await switchOrg(id)
    router.go(0)
  } catch (e) { console.error(e) }
}
const orgPrimary = computed(() => {
  const n = org.value?.legal_name || ''
  if (!n) return ''
  return n.split(' ')[0]
})
const orgSecondary = computed(() => {
  const n = org.value?.legal_name || ''
  if (!n) return ''
  return n.split(' ').slice(1).join(' ')
})

const pendingApprovals = ref(0)
async function refreshApprovals() {
  if (!session.value) { pendingApprovals.value = 0; return }
  const { count } = await supabase.from('audit_log').select('id', { count: 'exact', head: true })
    .eq('approval_status', 'pending')
    .neq('actor_user_id', session.value.user.id)
  pendingApprovals.value = count || 0
}

onMounted(async () => {
  const { data } = await supabase.auth.getSession()
  session.value = data.session
  if (session.value) { refreshApprovals(); loadOrgSettings(); loadMyOrgs() }
  supabase.auth.onAuthStateChange((event, s) => {
    const wasSignedIn = !!session.value
    session.value = s
    if (event === 'SIGNED_IN' && !wasSignedIn) logAuditEvent('login')
    if (s) { refreshApprovals(); loadOrgSettings(); loadMyOrgs() }
  })
})

async function logout() {
  await logAuditEvent('logout')
  await supabase.auth.signOut()
  router.push('/login')
}

const SALES = [
  { to: '/customers',    label: 'Customers',    icon: Users },
  { to: '/quotations',   label: 'Quotations',   icon: FileText },
  { to: '/invoices',     label: 'Invoices',     icon: Receipt },
  { to: '/payments',     label: 'Payments',     icon: Wallet },
  { to: '/credit-notes', label: 'Credit notes', icon: CreditCard }
]
const OPS_ALL = [
  { to: '/operations/ndt',      label: 'NDT Reports', icon: ClipboardCheck },
  { to: '/employees',           label: 'Employees',  icon: HardHat },
  { to: '/reports',             label: 'Reports',    icon: BarChart3 },
  { to: '/templates',           label: 'Templates',  icon: LayoutTemplate },
  { to: '/rt-jobs',             label: 'RT Jobs',    icon: Scan },
  { to: '/timesheets/generate', label: 'Timesheets', icon: CalendarDays },
]
const OPS = computed(() =>
  isTech.value
    ? OPS_ALL.filter(i => i.to.startsWith('/operations/'))
    : OPS_ALL
)
const SHOW_SALES = computed(() => !isTech.value)
const SHOW_FOOTER_ADMIN = computed(() => !isTech.value)
const isActive = (to: string) => route.path === to || route.path.startsWith(to + '/')

// --- Search palette ---
type Hit = { kind: 'customer' | 'invoice' | 'quotation' | 'credit_note'; id: string; title: string; subtitle: string; to: string }
const cmdOpen = ref(false)
const search = ref('')
const hits = ref<Hit[]>([])
const activeIdx = ref(0)
const searchLoading = ref(false)
const kindLabel: Record<Hit['kind'], string> = {
  customer: 'Customer', invoice: 'Invoice', quotation: 'Quotation', credit_note: 'Credit note'
}
const grouped = computed(() => {
  const g: Record<string, Hit[]> = {}
  for (const h of hits.value) (g[kindLabel[h.kind]] ||= []).push(h)
  return g
})

watch(cmdOpen, (v) => { if (!v) { search.value = ''; hits.value = [] } })

let timer: any
watch(search, (q) => {
  clearTimeout(timer)
  if (!q.trim()) { hits.value = []; return }
  timer = setTimeout(() => runSearch(q.trim()), 180)
})

async function runSearch(q: string) {
  searchLoading.value = true
  const like = `%${q}%`
  try {
    const [c, i, qu, cn] = await Promise.all([
      supabase.from('customers').select('id, legal_name, display_name').or(`legal_name.ilike.${like},display_name.ilike.${like}`).limit(6),
      supabase.from('invoices').select('id, invoice_no, status, customers(legal_name)').or(`invoice_no.ilike.${like}`).limit(6),
      supabase.from('quotations').select('id, quotation_no, status, customers(legal_name)').or(`quotation_no.ilike.${like}`).limit(6),
      supabase.from('credit_notes').select('id, credit_note_no, status, invoices(invoice_no)').or(`credit_note_no.ilike.${like}`).limit(6)
    ])
    const out: Hit[] = []
    for (const r of c.data || []) out.push({ kind: 'customer', id: r.id, title: r.display_name || r.legal_name, subtitle: r.legal_name, to: `/customers/${r.id}` })
    for (const r of i.data || []) out.push({ kind: 'invoice', id: r.id, title: r.invoice_no || '(draft)', subtitle: `${(r as any).customers?.legal_name || ''} · ${r.status}`, to: `/invoices/${r.id}` })
    for (const r of qu.data || []) out.push({ kind: 'quotation', id: r.id, title: r.quotation_no || '(draft)', subtitle: `${(r as any).customers?.legal_name || ''} · ${r.status}`, to: `/quotations/${r.id}` })
    for (const r of cn.data || []) out.push({ kind: 'credit_note', id: r.id, title: r.credit_note_no || '(draft)', subtitle: `Inv ${(r as any).invoices?.invoice_no || ''} · ${r.status}`, to: `/credit-notes/${r.id}` })
    hits.value = out
    activeIdx.value = 0
  } finally { searchLoading.value = false }
}

function onPaletteKey(e: KeyboardEvent) {
  if (!hits.value.length) return
  if (e.key === 'ArrowDown') { e.preventDefault(); activeIdx.value = Math.min(activeIdx.value + 1, hits.value.length - 1) }
  else if (e.key === 'ArrowUp') { e.preventDefault(); activeIdx.value = Math.max(activeIdx.value - 1, 0) }
  else if (e.key === 'Enter') { e.preventDefault(); const h = hits.value[activeIdx.value]; if (h) go(h) }
}

function go(h: Hit) {
  cmdOpen.value = false
  search.value = ''
  hits.value = []
  router.push(h.to)
}

function onGlobalKey(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    cmdOpen.value = true
  }
}
window.addEventListener('keydown', onGlobalKey)
onBeforeUnmount(() => window.removeEventListener('keydown', onGlobalKey))
</script>

<template>
  <div v-if="session && !route.meta.public">
    <SidebarProvider v-model:open="sidebarOpen">
      <Sidebar collapsible="icon" @click="onSidebarClick">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <template v-if="myOrgs.length > 1">
                <DropdownMenu>
                  <DropdownMenuTrigger as-child>
                    <SidebarMenuButton size="lg" :tooltip="org?.legal_name" class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                      <div class="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-md">
                        <Building2 class="size-4" />
                      </div>
                      <div class="grid flex-1 text-left text-sm leading-tight">
                        <span class="truncate font-semibold">{{ orgPrimary }}</span>
                        <span class="text-muted-foreground truncate text-xs">{{ orgSecondary }}</span>
                      </div>
                      <ChevronsUpDown class="ml-auto size-4 opacity-60" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right" align="start" class="w-64">
                    <DropdownMenuLabel>Switch organization</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      v-for="o in myOrgs" :key="o.id"
                      @select="onSwitchOrg(o.id)"
                    >
                      <Building2 class="size-4" />
                      <div class="flex flex-1 flex-col">
                        <span class="truncate text-sm">{{ o.name || o.legal_name }}</span>
                        <span class="text-muted-foreground truncate text-xs">{{ o.role }}</span>
                      </div>
                      <Check v-if="o.id === activeOrgId" class="ml-2 size-4" />
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem @select="createOrgOpen = true">
                      <Building2 class="size-4" />
                      Create organization
                    </DropdownMenuItem>
                    <DropdownMenuItem @select="router.push('/settings/org')">
                      <Building2 class="size-4" />
                      Org settings
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </template>
              <div v-else class="flex items-center gap-2 px-2 py-1.5">
                <div class="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-md">
                  <Building2 class="size-4" />
                </div>
                <div class="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span class="truncate font-semibold">{{ orgPrimary }}</span>
                  <span class="text-muted-foreground truncate text-xs">{{ orgSecondary }}</span>
                </div>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup v-if="SHOW_SALES">
            <SidebarGroupLabel>Sales</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem v-for="item in SALES" :key="item.to">
                  <SidebarMenuButton as-child :is-active="isActive(item.to)" :tooltip="item.label">
                    <router-link :to="item.to">
                      <component :is="item.icon" />
                      <span>{{ item.label }}</span>
                    </router-link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarGroup>
            <SidebarGroupLabel>Operations</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem v-for="item in OPS" :key="item.to">
                  <SidebarMenuButton as-child :is-active="isActive(item.to)" :tooltip="item.label">
                    <router-link :to="item.to">
                      <component :is="item.icon" />
                      <span>{{ item.label }}</span>
                    </router-link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem v-if="SHOW_FOOTER_ADMIN">
              <SidebarMenuButton as-child :is-active="isActive('/issues')" tooltip="Issues">
                <router-link to="/issues">
                  <CircleDot />
                  <span>Issues</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger as-child>
                  <SidebarMenuButton size="lg" :tooltip="session?.user?.email" class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                    <div class="bg-sidebar-accent text-sidebar-accent-foreground flex aspect-square size-8 items-center justify-center rounded-md">
                      <UserCircle2 class="size-4" />
                    </div>
                    <div class="grid flex-1 text-left text-sm leading-tight">
                      <span class="truncate font-medium">{{ (session?.user?.email || '').split('@')[0] }}</span>
                      <span class="text-muted-foreground truncate text-xs">{{ session?.user?.email }}</span>
                    </div>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" class="w-56">
                  <DropdownMenuLabel>{{ session?.user?.email }}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup v-if="SHOW_FOOTER_ADMIN">
                    <DropdownMenuItem @select="router.push('/settings/org')">
                      <Building2 class="size-4" />
                      Org settings
                    </DropdownMenuItem>
                    <DropdownMenuItem @select="router.push('/users')">
                      <Users class="size-4" />
                      Users
                    </DropdownMenuItem>
                    <DropdownMenuItem @select="router.push('/approvals')">
                      <Inbox class="size-4" />
                      Approvals
                      <span v-if="pendingApprovals" class="bg-primary text-primary-foreground ml-auto rounded-full px-1.5 text-xs">{{ pendingApprovals }}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem @select="router.push('/audit')">
                      <ShieldCheck class="size-4" />
                      Audit log
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator v-if="SHOW_FOOTER_ADMIN" />
                  <DropdownMenuItem @select="logout">
                    <LogOut class="size-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset @pointerdown="onInsetPointerDown">
        <header class="bg-background sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger />
          <Separator orientation="vertical" class="h-5" />
          <Button variant="outline" class="text-muted-foreground w-full max-w-md justify-start gap-2 font-normal" @click="cmdOpen = true">
            <Search class="size-4" />
            <span>Search…</span>
            <kbd class="bg-muted text-muted-foreground pointer-events-none ml-auto inline-flex h-5 select-none items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium">
              <span class="text-xs">⌘</span>K
            </kbd>
          </Button>
          <span
            class="bg-muted text-muted-foreground ml-auto rounded border px-2 py-0.5 font-mono text-[10px] leading-5"
            :title="`Build commit ${appCommit}`"
          >
            {{ appCommit }}
          </span>
        </header>
        <main :class="route.meta.fullHeight ? 'flex-1' : ((route.path.startsWith('/templates/') || route.meta.wide) ? 'flex-1 px-3 py-4 sm:px-4 sm:py-6' : 'mx-auto w-full max-w-6xl flex-1 px-3 py-4 sm:px-4 sm:py-6')">
          <router-view />
        </main>
      </SidebarInset>
    </SidebarProvider>

    <FeedbackBubble />

    <Dialog v-model:open="createOrgOpen">
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>You'll become its owner and it will become your active org.</DialogDescription>
        </DialogHeader>
        <div class="grid gap-3 py-2">
          <div class="grid gap-1.5">
            <label class="text-sm font-medium">Name</label>
            <Input v-model="createOrgForm.name" placeholder="ACME Pte Ltd" autofocus />
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium">Legal name <span class="text-muted-foreground text-xs">(optional, defaults to Name)</span></label>
            <Input v-model="createOrgForm.legal_name" />
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium">Address <span class="text-muted-foreground text-xs">(optional)</span></label>
            <Input v-model="createOrgForm.address" />
          </div>
          <div class="flex items-center gap-3">
            <input id="gst-reg" type="checkbox" v-model="createOrgForm.gst_registered" class="size-4" />
            <label for="gst-reg" class="text-sm">GST registered</label>
            <Input v-if="createOrgForm.gst_registered" v-model="createOrgForm.gst_rate" type="number" step="0.01" class="ml-2 w-24" />
            <span v-if="createOrgForm.gst_registered" class="text-muted-foreground text-xs">% rate</span>
          </div>
          <p v-if="createOrgErr" class="text-destructive text-sm">{{ createOrgErr }}</p>
        </div>
        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="createOrgOpen = false" :disabled="creatingOrg">Cancel</Button>
          <Button @click="submitCreateOrg" :disabled="creatingOrg">{{ creatingOrg ? 'Creating…' : 'Create' }}</Button>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog v-model:open="cmdOpen">
      <DialogContent class="top-1/4 translate-y-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader class="sr-only">
          <DialogTitle>Search</DialogTitle>
          <DialogDescription>Find customers, invoices, quotations and credit notes.</DialogDescription>
        </DialogHeader>
        <div class="border-b px-3 py-2">
          <div class="relative">
            <Search class="text-muted-foreground absolute left-2 top-1/2 size-4 -translate-y-1/2" />
            <Input
              v-model="search"
              autofocus
              placeholder="Search customers, invoices, quotations…"
              class="border-0 pl-8 shadow-none focus-visible:ring-0"
              @keydown="onPaletteKey"
            />
          </div>
        </div>
        <div class="max-h-96 overflow-y-auto p-1">
          <div v-if="!hits.length" class="text-muted-foreground px-3 py-6 text-center text-sm">
            {{ searchLoading ? 'Searching…' : (search ? 'No results.' : 'Type to search.') }}
          </div>
          <template v-else>
            <div v-for="(items, label) in grouped" :key="label">
              <div class="text-muted-foreground px-2 pb-1 pt-2 text-xs font-medium">{{ label }}</div>
              <button
                v-for="h in items" :key="h.kind + h.id"
                type="button"
                class="hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
                :class="hits.indexOf(h) === activeIdx && 'bg-accent text-accent-foreground'"
                @mouseenter="activeIdx = hits.indexOf(h)"
                @click="go(h)"
              >
                <span class="font-medium">{{ h.title }}</span>
                <span v-if="h.subtitle" class="text-muted-foreground truncate text-xs">{{ h.subtitle }}</span>
              </button>
            </div>
          </template>
        </div>
      </DialogContent>
    </Dialog>
  </div>
  <div v-else class="min-h-screen">
    <main :class="route.path.startsWith('/templates/') ? 'px-3 py-4 sm:px-4 sm:py-6' : 'mx-auto max-w-6xl px-3 py-4 sm:px-4 sm:py-6'"><router-view /></main>
  </div>
</template>
